import "./MaterialSymbol.css";
import type { TextColor } from "@ui/typography/Text";
import { resolveCssColor } from "@utils/resolveCssColor";
import type { Component, JSX } from "solid-js";
import { Show } from "solid-js";

export type MaterialSymbolRelativeSize =
	| "even-larger"
	| "plus"
	| "larger"
	| "smaller";

export type MaterialSymbolContainerProps = {
	/** CSS color, `var(--…)`, or token shorthand e.g. `grey-200`, `gvt-blue-500`. */
	background?: string;
	/** @default "0.5rem" */
	borderRadius?: string;
	/** Container width and height. @default "2.5rem" */
	size?: string;
	class?: string;
};

export type MaterialSymbolProps = {
	symbol: string;
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	/** @default false */
	filled?: boolean;
	/** Same token set as {@link Text} `color`. */
	color?: TextColor;
	/**
	 * Size relative to surrounding text.
	 * - `"smaller"` / `"larger"` / `"plus"` / `"even-larger"`: `1em` / `1.2em` /
	 *   `1.333em` / `1.5em`, via the stylesheet.
	 * - number: `font-size` as `<number>em`.
	 * @default 1.25 (via stylesheet when omitted)
	 */
	relativeSize?: MaterialSymbolRelativeSize | number;
	/**
	 * Centers the icon in a rounded container.
	 * Pass `true` for default sizing, or an object to customise background and dimensions.
	 */
	container?: boolean | MaterialSymbolContainerProps;
};

/**
 * The ligature name is drawn from `data-symbol` by the stylesheet rather than
 * written as text, and the icon is always hidden from assistive tech. As text,
 * names like "arrow_back" became part of the page's content for search
 * crawlers and of the accessible name of whatever button held the icon. An
 * icon that carries meaning on its own needs a label on its control.
 */

export const MaterialSymbol: Component<MaterialSymbolProps> = (props) => {
	const classList = () => ({
		...props.classList,
		[`gvtui-material-symbol--color-${props.color}`]: props.color !== undefined,
		"gvtui-material-symbol--relative-size-smaller":
			props.relativeSize === "smaller",
		"gvtui-material-symbol--relative-size-larger":
			props.relativeSize === "larger",
		"gvtui-material-symbol--relative-size-plus": props.relativeSize === "plus",
		"gvtui-material-symbol--relative-size-even-larger":
			props.relativeSize === "even-larger",
		"gvtui-material-symbol--filled": props.filled ?? false,
	});

	const style = () => {
		if (typeof props.relativeSize === "number") {
			return { ...props.style, "font-size": `${props.relativeSize}em` };
		}
		return props.style;
	};

	const containerOptions = (): MaterialSymbolContainerProps | undefined => {
		if (!props.container) {
			return undefined;
		}
		if (props.container === true) {
			return {};
		}
		return props.container;
	};

	const containerStyle = (): JSX.CSSProperties | undefined => {
		const options = containerOptions();
		if (!options) {
			return undefined;
		}

		return {
			...(options.background !== undefined
				? { background: resolveCssColor(options.background) }
				: {}),
			"border-radius": options.borderRadius ?? "0.5rem",
			width: options.size ?? "2.5rem",
			height: options.size ?? "2.5rem",
		};
	};

	const containerClass = () => {
		const options = containerOptions();
		return options?.class ?? "";
	};

	const ligature = () => {
		const name = props.symbol;
		if (!name || name === "undefined" || name === "null") {
			return "";
		}
		return name;
	};

	const symbol = () => (
		<span
			class={`gvtui-material-symbol ${props.class ?? ""}`}
			classList={classList()}
			style={style()}
			data-symbol={ligature() || undefined}
			aria-hidden="true"
		/>
	);

	return (
		<Show when={props.container} fallback={symbol()}>
			<div
				class={`gvtui-material-symbol-container ${containerClass()}`.trim()}
				style={containerStyle()}
				aria-hidden="true"
			>
				{symbol()}
			</div>
		</Show>
	);
};
